import { initializeApp, getApps, getApp } from 'firebase/app';
import type { FirebaseOptions } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { connectAuthEmulator } from 'firebase/auth';

/**
 * Firebase client config.
 *
 * Two sources, in priority order:
 *
 *  1. `VITE_FIREBASE_*` variables — what you use for local dev (.env.local)
 *     and what you can set explicitly in apphosting.yaml.
 *  2. `FIREBASE_WEBAPP_CONFIG` — Firebase App Hosting injects this at build
 *     time with the config of the backend's linked web app. vite.config.ts
 *     forwards it into the bundle as __FIREBASE_WEBAPP_CONFIG__, so a fresh
 *     App Hosting backend works with no env wiring at all.
 *
 * None of these are secrets; the client config is public by design and access
 * is controlled by firestore.rules / storage.rules.
 */

declare const __FIREBASE_WEBAPP_CONFIG__: string | undefined;

function readConfig(): FirebaseOptions {
  const env = import.meta.env;

  if (env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_PROJECT_ID) {
    return {
      apiKey: env.VITE_FIREBASE_API_KEY,
      authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: env.VITE_FIREBASE_APP_ID,
    };
  }

  const injected = typeof __FIREBASE_WEBAPP_CONFIG__ === 'string' ? __FIREBASE_WEBAPP_CONFIG__ : '';
  if (injected) {
    try {
      return JSON.parse(injected) as FirebaseOptions;
    } catch {
      // fall through to the error below — a malformed config should be loud
    }
  }

  /**
   * In dev, fall back to a placeholder so the app and the preview harness still
   * boot without a project wired up — every network call will fail, which is
   * the correct and obvious symptom. In a production build this always throws:
   * silently shipping a broken config would be far worse.
   */
  if (import.meta.env.DEV) {
    console.warn(
      '[firebase] No config found. Using a placeholder — nothing will load.\n' +
        'Copy .env.example to .env.local and fill in your web app config.',
    );
    return {
      apiKey: 'placeholder',
      authDomain: 'placeholder.firebaseapp.com',
      projectId: 'placeholder',
      storageBucket: 'placeholder.firebasestorage.app',
      appId: 'placeholder',
    };
  }

  throw new Error(
    'Firebase config missing. Copy .env.example to .env.local and fill in your ' +
      'web app config, or deploy on Firebase App Hosting (which injects it).',
  );
}

export const firebaseApp = getApps().length ? getApp() : initializeApp(readConfig());

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

export const googleProvider = new GoogleAuthProvider();

/** `npm run emulators` + VITE_USE_EMULATORS=1 to develop without touching prod. */
if (import.meta.env.VITE_USE_EMULATORS === '1' && !('__emulatorsBound' in globalThis)) {
  Object.defineProperty(globalThis, '__emulatorsBound', { value: true });
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}
